import { DestroyRef, Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CommandRegistryService } from '../../core/services/command-registry.service';
import type { Command } from '../../core/services/command-registry.service';
import { FlowRouteService } from '../../core/services/flow-route.service';
import { isAdminTabUrl } from '../../core/services/admin-tabs.utils';
import { AiTriggerRouterService } from './ai-trigger-router.service';

/**
 * RESPONSIBILITY: the three AI surfaces Command K's own Ask row does NOT
 * cover, published as palette rows so removing the top-bar Ask trigger does
 * not strand them.
 *
 * The old trigger branched to the admin helper, Flow Intelligence, full-page
 * `/ai`, or the docked Workplace panel. Command K's own Ask replaces the
 * docked-panel entry point; these three rows retain the other destinations.
 *
 * COMMANDS:
 *   action:ai-admin-helper — inside /admin
 *   action:ai-flow         — inside /flow
 *   action:ai-full-page    — everywhere else: opens Ask IN PLACE (the id is
 *                            historical; it went to `/ai` until 4 Oct 2026)
 *
 * Exactly one is visible at a time, because each surface has exactly one
 * "open the assistant" meaning. Offering all three everywhere would ask the
 * user to know which AI they wanted before they knew what they wanted.
 *
 * BACKEND CONTRACT: none. Every row delegates to AiTriggerRouterService, the
 * same owner as the shell chord and onboarding primer.
 *
 * Lives under `features/shell` alongside the other command contributors and
 * the shared route-aware AI trigger.
 *
 * AI CONTEXT
 *
 * **Registered from the shell, not from a page.** The gen-1 Home's
 * `ArticleCommandsService` was registered only by that Home's summary page,
 * so its commands never existed until the user had visited it (both were
 * deleted with that Home, 4 October 2026). These have to exist on every
 * surface — an admin who never opens Home would otherwise have no way to
 * reach the admin helper at all, which is the exact failure this service is
 * here to prevent.
 *
 * **`visible()` reads the router directly.** `AppContext` carries
 * `currentSection` and `userActions` but no admin flag, and `AppSection` has
 * no `'admin'` member — admin is a URL shape, tested by `isAdminTabUrl`. The
 * same helper the shell uses is reused rather than a second predicate that
 * could drift from it.
 */
@Injectable({ providedIn: 'root' })
export class CmdkAiCommandsService {
  private readonly registry = inject(CommandRegistryService);
  private readonly router = inject(Router);
  private readonly flowRoute = inject(FlowRouteService);
  private readonly aiTrigger = inject(AiTriggerRouterService);
  private readonly destroyRef = inject(DestroyRef);

  private registered = false;

  /** Call once, from the shell. Idempotent. */
  register(): void {
    if (this.registered) return;
    this.registered = true;

    const inAdmin = (): boolean => isAdminTabUrl(this.router.url);

    const commands: Command[] = [
      {
        id: 'action:ai-admin-helper',
        title: 'Ask about admin',
        subtitle: 'Open the admin AI helper for this section',
        aliases: ['ai', 'assistant', 'help', 'admin ai', 'copilot'],
        icon: 'sparkles',
        group: 'ai',
        shortcut: '⌘J',
        score: 70,
        visible: () => this.aiTrigger.available() && inAdmin(),
        execute: () => this.aiTrigger.route('command-palette'),
      },
      {
        id: 'action:ai-flow',
        title: 'Open Flow Intelligence',
        subtitle: 'Ask about this flow',
        aliases: ['ai', 'assistant', 'flow ai', 'intelligence'],
        icon: 'sparkles',
        group: 'ai',
        shortcut: '⌘J',
        score: 70,
        visible: () => this.aiTrigger.available() && !inAdmin() && this.flowRoute.inFlow(),
        execute: () => this.aiTrigger.route('command-palette'),
      },
      {
        id: 'action:ai-full-page',
        // Ask, by its own name and mark (Nath, 4 Oct: no "Workplace AI" in
        // the palette). The old name stays findable as an alias.
        title: 'Open Ask',
        subtitle: 'Ask beside what you are doing',
        aliases: ['ai', 'assistant', 'chat', 'ask', 'workplace ai'],
        icon: 'ask-mark',
        group: 'ai',
        shortcut: '⌘J',
        score: 60,
        visible: () => this.aiTrigger.available() && !inAdmin() && !this.flowRoute.inFlow(),
        // OPEN, never toggle: choosing "Open Ask" while Ask is showing must not
        // close it (`route` toggles, as ⌘J does).
        execute: () => this.aiTrigger.open('command-palette'),
      },
    ];

    this.destroyRef.onDestroy(this.registry.registerAll(commands));
  }
}
